import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from '../components/layout/AppLayout';
import { supabase } from '../lib/supabaseClient';
import { fetchAllUserData, convertToCSV, downloadCSV } from '../lib/exportUtils';
import { logger } from '../lib/logger';
import {
    User,
    Settings as SettingsIcon,
    Trash2,
    LogOut,
    Link as LinkIcon,
    Mail,
    ShieldCheck,
    Info,
    CheckCircle2,
    AlertCircle,
    ChevronRight,
    UserMinus,
    Users,
    Download,
    Bug,
    X,
    Copy,
    RefreshCw
} from 'lucide-react';

export default function Settings() {
    const { user, signOut } = useAuth();
    const [clearing, setClearing] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [partnerEmail, setPartnerEmail] = useState('');
    const [linking, setLinking] = useState(false);
    const [partner, setPartner] = useState<any>(null);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [showLogs, setShowLogs] = useState(false);
    const [logs, setLogs] = useState<any[]>([]);

    useEffect(() => {
        fetchPartner();
    }, [user]);

    // Logs refresher
    useEffect(() => {
        if (showLogs) {
            setLogs(logger.getLogs());
            const interval = setInterval(() => {
                setLogs(logger.getLogs());
            }, 2000);
            return () => clearInterval(interval);
        }
    }, [showLogs]);

    const fetchPartner = async () => {
        if (!user) return;
        const { data, error } = await supabase
            .from('user_links')
            .select(`
                partner_id,
                partner:profiles!partner_id(email, display_name)
            `)
            .eq('user_id', user.id)
            .maybeSingle();

        if (data && !error) {
            setPartner(data.partner);
        }
    };

    const handleUnlinkPartner = async () => {
        if (!user || !partner || linking) return;
        if (!window.confirm(`Are you sure you want to disconnect from ${partner.display_name}? You will lose access to the Shared Feed.`)) return;

        setLinking(true);
        setMessage(null);
        logger.info('Unlinking partner initiated');

        try {
            const { error } = await supabase
                .from('user_links')
                .delete()
                .or(`user_id.eq.${user.id},and(user_id.eq.${partner.id},partner_id.eq.${user.id})`);

            if (error) {
                // Fallback delete
                const { error: myError } = await supabase
                    .from('user_links')
                    .delete()
                    .match({ user_id: user.id, partner_id: partner.id });
                if (myError) throw myError;
            }

            setPartner(null);
            setMessage({ type: 'success', text: 'Partner disconnected successfully.' });
            logger.success('Partner disconnected');
        } catch (error: any) {
            logger.error('Error unlinking partner', error);
            setMessage({ type: 'error', text: 'Error unlinking: ' + error.message });
        } finally {
            setLinking(false);
        }
    };

    const handleLinkPartner = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user || linking) return;

        setLinking(true);
        setMessage(null);
        logger.info(`Attempting to link with ${partnerEmail}`);

        try {
            const { data: partnerProfile, error: profileError } = await supabase
                .from('profiles')
                .select('id, email, display_name')
                .eq('email', partnerEmail.trim().toLowerCase())
                .single();

            if (profileError || !partnerProfile) {
                logger.warn('Partner not found', { email: partnerEmail });
                setMessage({ type: 'error', text: 'User not found. Ensure they have signed up first.' });
                setLinking(false);
                return;
            }

            if (partnerProfile.id === user.id) {
                setMessage({ type: 'error', text: 'You cannot link with yourself.' });
                setLinking(false);
                return;
            }

            const { error: linkError } = await supabase
                .from('user_links')
                .upsert({ user_id: user.id, partner_id: partnerProfile.id }, { onConflict: 'user_id, partner_id' });

            if (linkError) throw linkError;

            await supabase
                .from('user_links')
                .upsert({ user_id: partnerProfile.id, partner_id: user.id }, { onConflict: 'user_id, partner_id' });

            setMessage({ type: 'success', text: `Successfully linked with ${partnerProfile.display_name}!` });
            setPartner(partnerProfile);
            setPartnerEmail('');
            logger.success('Partner linked successfully');
        } catch (error: any) {
            logger.error('Link partner failed', error);
            setMessage({ type: 'error', text: error.message });
        } finally {
            setLinking(false);
        }
    };

    const handleExportData = async () => {
        if (!user) return;
        setExporting(true);
        logger.info('Starting data export...');

        try {
            const sessions = await fetchAllUserData();
            const csv = convertToCSV(sessions);
            const filename = `jointsession_export_${new Date().toISOString().split('T')[0]}.csv`;
            downloadCSV(csv, filename);
            logger.success(`Data exported: ${sessions.length} records`);
            setMessage({ type: 'success', text: 'Export downloaded successfully.' });
        } catch (error: any) {
            logger.error('Export failed', error);
            setMessage({ type: 'error', text: 'Export failed: ' + error.message });
        } finally {
            setExporting(false);
        }
    };

    const handleClearSessions = async () => {
        if (!user) return;
        if (!window.confirm('Delete all your session history? This cannot be undone.')) return;

        setClearing(true);
        logger.info('Clearing all sessions');

        const { error } = await supabase
            .from('sessions')
            .delete()
            .eq('user_id', user.id);

        if (error) {
            logger.error('Clear sessions failed', error);
            setMessage({ type: 'error', text: 'Error clearing sessions: ' + error.message });
        } else {
            logger.success('Sessions cleared');
            setMessage({ type: 'success', text: 'All sessions successfully purged.' });
        }
        setClearing(false);
    };

    return (
        <AppLayout>
            <div className="max-w-xl mx-auto pb-24 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
                <div className="flex items-center gap-3">
                    <div className="bg-accent-purple/20 p-3 rounded-2xl">
                        <SettingsIcon className="text-accent-purple" size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-black italic">Settings</h1>
                        <p className="text-[10px] font-black uppercase tracking-widest text-dark-muted">Preferences & Privacy</p>
                    </div>
                </div>

                <div className="space-y-6">
                    {message && (
                        <div className={`p-4 rounded-2xl flex items-center gap-3 animate-in fade-in zoom-in-95 duration-300 ${message.type === 'success' ? 'bg-green-500/10 border border-green-500/20 text-green-500' : 'bg-accent-red/10 border border-accent-red/20 text-accent-red'
                            }`}>
                            {message.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                            <p className="text-[11px] font-bold">{message.text}</p>
                        </div>
                    )}

                    {/* Account Section */}
                    <div className="card bg-dark-surface/40 backdrop-blur-md border-dark-border/40 space-y-6">
                        <div className="flex items-center gap-2">
                            <User className="text-accent-purple" size={18} />
                            <h2 className="text-[11px] font-black uppercase tracking-[0.2em] text-white">Identity Management</h2>
                        </div>
                        <div className="bg-dark-bg/40 p-5 rounded-2xl border border-white/5 space-y-3">
                            <div className="flex items-center justify-between">
                                <p className="text-[10px] font-black text-dark-muted uppercase tracking-widest">Email Address</p>
                                <span className="bg-accent-purple/10 text-accent-purple px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-tighter">Verified</span>
                            </div>
                            <p className="text-sm font-bold text-white truncate">{user?.email}</p>
                        </div>
                    </div>

                    {/* Duo Connection Section */}
                    <div className="card bg-dark-surface/40 backdrop-blur-md border-dark-border/40 space-y-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Users className="text-accent-purple" size={18} />
                                <h2 className="text-[11px] font-black uppercase tracking-[0.2em] text-white">Duo Connection</h2>
                            </div>
                            {partner && (
                                <div className="flex items-center gap-1.5 px-2 py-1 bg-green-500/10 rounded-full border border-green-500/20">
                                    <div className="w-1 h-1 bg-green-500 rounded-full animate-pulse" />
                                    <span className="text-[9px] font-black text-green-500 uppercase tracking-tighter">Active Sync</span>
                                </div>
                            )}
                        </div>

                        {partner ? (
                            <div className="bg-dark-bg/40 p-5 rounded-2xl border border-accent-purple/20 space-y-4">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-accent-purple/20 to-accent-blue/20 flex items-center justify-center border border-white/5">
                                        <span className="text-xl font-black text-accent-purple uppercase">{partner.display_name?.[0]}</span>
                                    </div>
                                    <div>
                                        <p className="text-sm font-black text-white">{partner.display_name}</p>
                                        <p className="text-[10px] font-bold text-dark-muted">{partner.email}</p>
                                    </div>
                                </div>
                                <div className="pt-4 border-t border-dark-border/30 flex justify-between items-center gap-4">
                                    <p className="text-[10px] font-bold text-dark-muted leading-relaxed italic flex-1">
                                        "Connected accounts share sessions."
                                    </p>
                                    <button
                                        onClick={handleUnlinkPartner}
                                        disabled={linking}
                                        className="shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-lg bg-accent-red/5 hover:bg-accent-red/10 border border-accent-red/10 hover:border-accent-red/30 text-accent-red/60 hover:text-accent-red transition-all text-[10px] font-black uppercase tracking-widest"
                                    >
                                        {linking ? '...' : <><UserMinus size={12} /> Disconnect</>}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={handleLinkPartner} className="space-y-4">
                                <p className="text-xs text-dark-muted leading-relaxed font-medium">
                                    Connect with your partner to enable the **Duo Hub** and share your sessions for side-by-side comparison.
                                </p>
                                <div className="space-y-2 group">
                                    <label className="text-[10px] font-black text-dark-muted uppercase tracking-widest ml-1 group-focus-within:text-accent-purple transition-colors">Partner Email</label>
                                    <div className="relative">
                                        <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-dark-muted" />
                                        <input
                                            type="email"
                                            value={partnerEmail}
                                            onChange={(e) => setPartnerEmail(e.target.value)}
                                            className="input h-14 pl-12 bg-dark-bg/50 border-white/5 focus:border-accent-purple/50 text-sm font-bold"
                                            placeholder="partner@example.com"
                                            required
                                        />
                                    </div>
                                </div>
                                <button
                                    type="submit"
                                    disabled={linking}
                                    className="w-full h-12 bg-white text-dark-bg rounded-2xl font-black text-[11px] uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-white/90 active:scale-95 transition-all disabled:opacity-50"
                                >
                                    {linking ? <div className="w-4 h-4 border-2 border-dark-bg/20 border-t-dark-bg rounded-full animate-spin" /> : <><LinkIcon size={14} /> Link Partner</>}
                                </button>
                            </form>
                        )}
                    </div>

                    {/* Data & Export Section */}
                    <div className="card bg-dark-surface/40 backdrop-blur-md border-dark-border/40 space-y-6">
                        <div className="flex items-center gap-2">
                            <ShieldCheck className="text-accent-purple" size={18} />
                            <h2 className="text-[11px] font-black uppercase tracking-[0.2em] text-white">Vault & Logs</h2>
                        </div>
                        <div className="space-y-3">
                            <button
                                onClick={handleExportData}
                                disabled={exporting}
                                className="w-full flex items-center justify-between p-4 bg-dark-bg/40 rounded-2xl border border-white/5 hover:bg-accent-purple/10 hover:border-accent-purple/30 group transition-all"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="p-2 bg-dark-bg rounded-xl group-hover:bg-accent-purple/20 transition-colors">
                                        {exporting ? <RefreshCw size={16} className="text-accent-purple animate-spin" /> : <Download size={16} className="text-dark-muted group-hover:text-accent-purple transition-colors" />}
                                    </div>
                                    <div className="text-left">
                                        <p className="font-bold text-sm text-white">Export Vault Data</p>
                                        <p className="text-[10px] font-medium text-dark-muted">Download CSV of all recorded sessions</p>
                                    </div>
                                </div>
                                <ChevronRight size={16} className="text-dark-muted group-hover:translate-x-1 transition-transform" />
                            </button>

                            <button
                                onClick={() => setShowLogs(true)}
                                className="w-full flex items-center justify-between p-4 bg-dark-bg/40 rounded-2xl border border-white/5 hover:bg-white/5 group transition-all"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="p-2 bg-dark-bg rounded-xl">
                                        <Bug size={16} className="text-dark-muted" />
                                    </div>
                                    <div className="text-left">
                                        <p className="font-bold text-sm text-white">System Logs</p>
                                        <p className="text-[10px] font-medium text-dark-muted">View app events for troubleshooting</p>
                                    </div>
                                </div>
                                <ChevronRight size={16} className="text-dark-muted group-hover:translate-x-1 transition-transform" />
                            </button>

                            <button
                                onClick={handleClearSessions}
                                disabled={clearing}
                                className="w-full flex items-center justify-between p-4 bg-dark-bg/40 rounded-2xl border border-white/5 hover:bg-accent-red/5 hover:border-accent-red/20 group transition-all"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="p-2 bg-dark-bg rounded-xl group-hover:bg-accent-red/10 transition-colors">
                                        <Trash2 size={16} className="text-dark-muted group-hover:text-accent-red transition-colors" />
                                    </div>
                                    <div className="text-left">
                                        <p className="font-bold text-sm text-white">Clear Sessions</p>
                                        <p className="text-[10px] font-medium text-dark-muted">Purge all logs from your personal vault</p>
                                    </div>
                                </div>
                                <ChevronRight size={16} className="text-dark-muted group-hover:translate-x-1 transition-transform" />
                            </button>

                            <button
                                onClick={signOut}
                                className="w-full flex items-center justify-between p-4 bg-dark-bg/40 rounded-2xl border border-white/5 hover:bg-white/5 group transition-all"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="p-2 bg-dark-bg rounded-xl">
                                        <LogOut size={16} className="text-dark-muted" />
                                    </div>
                                    <div className="text-left">
                                        <p className="font-bold text-sm text-white">Sign Out</p>
                                        <p className="text-[10px] font-medium text-dark-muted">End current secure vault session</p>
                                    </div>
                                </div>
                                <ChevronRight size={16} className="text-dark-muted group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                    </div>

                    {/* About Section */}
                    <div className="card bg-dark-surface/40 backdrop-blur-md border-dark-border/40 space-y-4 text-center">
                        <div className="flex justify-center">
                            <div className="p-2 bg-accent-purple/10 rounded-xl">
                                <Info size={16} className="text-accent-purple" />
                            </div>
                        </div>
                        <p className="text-[11px] font-black uppercase tracking-[0.2em] text-white">JointSession v1.2</p>
                        <p className="text-[10px] font-medium text-dark-muted leading-relaxed">
                            A private ecosystem for two.<br />
                            Crafted with ❤️ for our shared journey.
                        </p>
                    </div>
                </div>

                {/* Log Viewer Modal */}
                {showLogs && (
                    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                        <div className="bg-dark-surface w-full max-w-2xl h-[80vh] rounded-3xl border border-white/10 flex flex-col shadow-2xl relative">
                            <div className="p-5 border-b border-white/10 flex items-center justify-between">
                                <h3 className="text-lg font-black uppercase tracking-widest flex items-center gap-3">
                                    <Bug className="text-accent-purple" /> System Diagnostic
                                </h3>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => {
                                            const text = logs.map(l => `[${l.type.toUpperCase()}] ${l.message} (${l.timestamp})`).join('\n');
                                            navigator.clipboard.writeText(text);
                                            logger.info('Logs copied to clipboard');
                                        }}
                                        className="p-2 hover:bg-white/5 rounded-full"
                                        title="Copy to Clipboard"
                                    >
                                        <Copy size={18} />
                                    </button>
                                    <button
                                        onClick={() => setShowLogs(false)}
                                        className="p-2 hover:bg-white/5 rounded-full"
                                    >
                                        <X size={24} />
                                    </button>
                                </div>
                            </div>
                            <div className="flex-1 overflow-auto p-4 space-y-2 font-mono text-xs">
                                {logs.length === 0 && (
                                    <div className="text-center text-dark-muted py-10">No logs recorded yet.</div>
                                )}
                                {logs.map((log) => (
                                    <div key={log.id} className="border-b border-white/5 pb-2 mb-2 last:border-0">
                                        <div className="flex gap-2 mb-1">
                                            <span className={`font-bold uppercase tracking-tight text-[10px] px-1.5 rounded ${log.type === 'error' ? 'bg-accent-red/20 text-accent-red' :
                                                log.type === 'warn' ? 'bg-amber-500/20 text-amber-500' :
                                                    log.type === 'success' ? 'bg-green-500/20 text-green-500' :
                                                        'bg-blue-500/20 text-blue-500'
                                                }`}>
                                                {log.type}
                                            </span>
                                            <span className="text-dark-muted opacity-50">{new Date(log.timestamp).toLocaleTimeString()}</span>
                                        </div>
                                        <p className="text-white/90 break-words font-medium">{log.message}</p>
                                        {log.data && (
                                            <pre className="mt-1 p-2 bg-black/30 rounded text-dark-muted overflow-auto max-h-40">
                                                {log.data}
                                            </pre>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <div className="p-4 border-t border-white/10 bg-dark-bg/20 rounded-b-3xl">
                                <button
                                    onClick={() => { logger.clear(); setLogs([]); }}
                                    className="w-full py-3 text-xs font-bold uppercase tracking-widest text-dark-muted hover:text-accent-red transition-colors"
                                >
                                    Clear Log History
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AppLayout>
    );
}
